import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import i18next from 'i18next';
import { withNamespaces } from 'react-i18next';
import * as storage from '@storage'
import { isIOS, formatDate, toDisplayDecimal, toDecimal, getDescriptionWeekDay } from '@utils';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import LoadingLayer from '@controls/LoadingLayer';
import AlertAlgo from '@controls/AlertSnack';
import Popup from '@controls/Popup'
import styles from '@styles/punchApproval.module.css';
import { IsEnglish, GetLanguage } from '@utils/language';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-duotone-svg-icons";
import { punchApprove } from "@actionsSup/punchApproval";
import { getPunchApprovalList } from '@actionsSup/punchApproval';
import * as StatusCode from '@scripts/StatusCode';
import * as StatusEntries from '@scripts/StatusEntries';
import * as StatusApprove from "@scripts/PunchApproveStatus";
import * as ApprobationStructure from '@scripts/ApprobationStructure';
import { sleep } from '@utils/index';

const useStyles = makeStyles({
    appBar: {
        top: 'auto',
        bottom: '76px',
        height: '35px!important',
        boxShadow: '0 9px 20px 0 !important',
        backgroundColor: 'var(--color1) !important',
        color: 'var(--color2) !important',
        '@media (min-width: 600px)': {
            bottom: '75px',
        },
    },
    appBarIOS: {
        bottom: '76px',
    },
    total: {
        minHeight: '35px',
    },
    divAction: {
        marginTop: '15px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    icons: {
        paddingBottom: '10px!important',
        color: '#3f51b5 !important'
    },
    labelDay: {
        padding: '0px 13px',
        color: '#000000',
        marginLeft: '10px',
        display: 'table',
        float: 'left'
    },
    totalHourDay: {
        backgroundColor: '#44cdff',
        borderRadius: '8px',
        padding: '0px 13px',
        color: '#ffffff',
        marginLeft: '10px',
        marginRight: '10px',
        display: 'table',
        float: 'right'
    },
    divHeaderSection: {
        display: 'table',
        width: '100%',
        '&:not(:first-child)': {
            marginTop: '15px'
        }
    },
    divApproveIcon: {
        position: 'absolute',
        right: '28px',
        zIndex: '10000',
        marginTop: '10px',
        cursor: 'pointer'
    },
    icon: {
        fontSize: '28px'
    },
    iconApproved: {
        color: '#008000'
    },
    divSection: {
        marginTop: '12px'
    },
    divError: {
        backgroundColor: '#ffc8c8',
        color: 'red',
        borderRadius: '5px',
        padding: '10px',
        marginTop: '5px',
        display: 'table'
    },
    divWarning: {
        backgroundColor: '#fdf2ab',
        color: '#796900',
        borderRadius: '5px',
        padding: '10px',
        marginTop: '5px',
        display: 'table'
    },
    spanTitle: {
        fontWeight: 'bold',
        float: 'left',
        fontSize: '14px'
    },
    spanDesc: {
        float: 'left',
        marginLeft: '5px',
        fontSize: '14px'
    },
    divInput: {
        display: 'table'
    }
});

function PunchDaysDetail(props) {

    const classes = useStyles();
    const { targetDate, onEditPunch, data, onLoadComplete, level, employeeCode, startDate, endDate, forceReload, setForceReload, handleStatusPunchList, isPunchFormError, setIsPunchFormError } = props;

    const alertRef = useRef(null);

    const [dataDetail, setDataDetail] = React.useState(null);
    const [total, setTotal] = React.useState(0);
    const [isiOS, setIsiOS] = React.useState(isIOS());
    const [isProcessing, setIsProcessing] = React.useState(false);

    const useDoubleApprobation = storage.getUseDoubleApprobation();
    const approbationStructure = storage.getApprobationStructure();
    const isLevel2Approbator = storage.getIsLevel2Approbator();
    const isLevel1ApprobationMandatory = storage.getIsApprobationLv1Mandatory();
    const isUseAbc = storage.getIsUseAbc();
    const isPayProject = storage.getIsProjectEntry();
    const isPayActivity = storage.getIsActivityEntry();
    const isPayTask = storage.getIsTaskEntry();

    useEffect(() => {
        loadData();
        setIsProcessing(true);
        setTimeout(function () {            
            setIsProcessing(false);
        }, 1000);
    }, [data]);

    useEffect(() => {
        const onForceReload = async () => {
            setIsProcessing(true);
            await reload();
            setIsProcessing(false);
            setForceReload(false);
        }

        if (forceReload) {
            onForceReload();
        }
        
    }, [forceReload]);

    useLayoutEffect(() => {
        resizeForm();
    }, []);

    const reload = async () => {

        let dataApproved = null;
        let dataNotApproved = null;        

        await sleep(500);

        let response = await getPunchApprovalList(startDate, endDate);

        if (response != null && response.status == StatusCode.STATUS_OK) {
            if (response.result.length > 0) {
                if (!useDoubleApprobation) {
                    dataNotApproved = response.result.filter(x => x.lst2.length > 0);;
                    dataApproved = response.result.filter(x => x.lstAp.length > 0 &&
                        x.lst2.length == 0);
                } else {
                    if (approbationStructure == ApprobationStructure.DEPARTMENT) {
                        dataNotApproved = response.result.filter(x => x.lst1.length > 0 || x.lst2.length > 0);
                        dataApproved = response.result.filter(x => x.lstAp.length > 0 &&
                            x.lst1.length == 0 &&
                            x.lst2.length == 0);
                    } else {
                        dataNotApproved = response.result.filter(x => x.lst1.length > 0 || x.lst2.length > 0);
                        dataApproved = response.result.filter(x => x.lstAp.length > 0 &&
                            x.lst1.length == 0 &&
                            x.lst2.length == 0);
                    }
                }
            }

            let dataDetails = dataNotApproved?.filter(x => x.ec == employeeCode)[0];
            if (!dataDetails || dataDetails.length === 0) {
                dataDetails = dataApproved?.filter(x => x.ec == employeeCode)[0];
            }

            handleStatusPunchList(dataDetails, level);
            loadData(true, dataDetails);            
            
            if (isPunchFormError) {
                alertRef.current.showMessageError(i18next.t('error:generic-error'));
                setIsPunchFormError(false);
            } else {
                alertRef.current.showMessageSuccess(i18next.t('punchApproval:message.update-success'));        
            }            
        } else {
            alertRef.current.showMessageError(i18next.t('error:generic-error'));
        }
    };

    const resizeForm = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const headerElement = document.querySelector('[data-id=headerPunchDetail]');

            if (headerElement != null) {
                const header = headerElement.offsetHeight;
                const offSet = 60;

                const customHeight = height - header - offSet;

                const element = document.querySelector('#divListPunchDetail');

                if (element != null) {
                    element.style.height = `${customHeight}px`;
                    element.style.overflowX = 'hidden';
                }
            }
        }, 1000);
    }

    const handleClick = (id, idCrypt) => {
        // TODO - DO NOT REMOVE THIS SECTION, IT WILL BE DEVELOPPED LATER
        //if (data.filter(x => x.timesheetId == id)[0].isCharged == true) {
        // If is already charged we don't allow to edit
        //return;
        //}

        if (!storage.getAllowConsultation()) {
            onEditPunch(id, idCrypt);
        }
    }

    const handleApprove = async (event, id, level, index, index2, isApprove) => {
        event.stopPropagation();

        const arrayId = [{ punchId: id, level: level }];

        var response = await punchApprove(arrayId, isApprove);

        const result = handleResponsePunch(response, isApprove);

        const isApproveStatus = result == StatusApprove.STATUS_APPROVED;

        setDataDetail(prevData => {
            const newData = [...prevData]; // copy array

            const pId = newData[index].list[index2].pchId;
            const objWarning = response.filter(x => JSON.parse(x).obj.punchCrypt == pId && JSON.parse(x).warning == true);
            const objError = response.filter(x => JSON.parse(x).obj.punchCrypt == pId && JSON.parse(x).error == true && JSON.parse(x).warning == false);
            const messageWarning = objWarning.length > 0 ? JSON.parse(objWarning).message : '';
            const messageError = objError.length > 0 ? JSON.parse(objError).message : '';

            let messageFinal = '';

            if (objWarning.length > 0) {
                messageFinal = messageWarning;
            } else if (objError.length > 0) {
                messageFinal = messageError;
            }

            newData[index].list[index2].message = messageFinal;

            let status = isApprove ? StatusApprove.STATUS_APPROVED : StatusApprove.STATUS_NOT_APPROVED;

            if (isApproveStatus != true) {
                if (objWarning.length > 0) {
                    status = StatusApprove.STATUS_WARNING;
                } else if (objError.length > 0) {
                    status = StatusApprove.STATUS_ERROR;
                }
            }

            //newData[index].list[index2].status = isApproveStatus == true ? (messageWarning.length > 0 ? StatusApprove.STATUS_WARNING : (messageError.length > 0 ? StatusApprove.STATUS_ERROR : StatusApprove.STATUS_APPROVED)) : StatusApprove.STATUS_NOT_APPROVED;
            newData[index].list[index2].status = status;

            // Here is to set isAp = true if all the punchs are approved
            let isApproved;

            if (useDoubleApprobation) {
                if (approbationStructure == ApprobationStructure.DEPARTMENT) {
                    if (isLevel2Approbator) {
                        // I'm level 2 approbator
                        newData[index].list[index2].isAp = isApproveStatus;
                        isApproved = newData[index].list.every(item => item.isAp);
                        // Here is to update list of the parent page
                        if (newData[index].data.lst.length > 0 && newData[index].data.lst.some(x => x.pchId == pId)) {
                            newData[index].data.lst.filter(x => x.pchId == pId)[0].isAp = isApproveStatus;
                            newData[index].data.lst.filter(x => x.pchId == pId)[0].status = status;
                        }
                        if (newData[index].data.lst1.length > 0 && newData[index].data.lst1.some(x => x.pchId == pId)) {
                            newData[index].data.lst1.filter(x => x.pchId == pId)[0].isAp = isApproveStatus;
                            newData[index].data.lst1.filter(x => x.pchId == pId)[0].status = status;
                        }
                        if (newData[index].data.lst2.length > 0 && newData[index].data.lst2.some(x => x.pchId == pId)) {
                            newData[index].data.lst2.filter(x => x.pchId == pId)[0].isAp = isApproveStatus;
                            newData[index].data.lst2.filter(x => x.pchId == pId)[0].status = status;
                        }
                    } else {
                        // I'm level 1 approbator
                        newData[index].list[index2].isAp1 = isApproveStatus;
                        isApproved = newData[index].list.every(item => item.isAp1);
                        // Here is to update list of the parent page
                        newData[index].data.lst1.filter(x => x.pchId == pId)[0].isAp1 = isApproveStatus;
                    }
                }
            } else {
                isApproved = newData[index].list.every(item => item.isAp);
                newData[index].data.isAp = isApproved;
            }

            newData[index] = {
                ...newData[index],         // copy object
            };

            return newData;
        });

    }

    const handleResponsePunch = (response, isApprove) => {
        let status = isApprove ? StatusApprove.STATUS_APPROVED : StatusApprove.STATUS_NOT_APPROVED;

        let qtySuccess = 0;
        let qtyFailed = 0;

        response.forEach(item => {
            const obj = JSON.parse(item);

            if (obj.error == true) {
                qtyFailed++;
            } else if (obj.success) {
                qtySuccess++;
            }
        });

        if (qtyFailed > 0 && qtySuccess > 0) {
            status = StatusApprove.STATUS_PARTIAL_APPROVED;
        } else if (qtyFailed > 0) {
            status = StatusApprove.STATUS_NOT_APPROVED;
        }

        return status;
    }

    const loadData = (forceReload = false, pData = null) => {

        if (!forceReload) {
            pData = data;
        }

        let formatData = [];
        let punchData = [];
        let totalHour = 0;

        if (pData) {
            if (useDoubleApprobation) {
                if (level == 1) {
                    punchData = pData.lst1;
                    totalHour = pData.th1;
                } else if (level == 2) {
                    punchData = pData.lst2;
                    totalHour = pData.th2;
                } else {
                    punchData = pData.lst;
                    totalHour = pData.thAp;
                }
            } else {
                punchData = pData.lst;
                totalHour = pData.th;
            }
        }
        
        formatData.push({
            totalHours: totalHour,
            list: punchData.sort((a, b) => new Date(a.sdt) - new Date(b.sdt)),
            data: pData
        });

        setDataDetail(formatData);
        setTotal(totalHour);
        onLoadComplete(false);
    }

    const getStartDate = (punch) => {
        return (
            punch.sdt != null &&
            <>
                <span className={classes.spanTitle}>
                    {`${i18next.t('punchApproval:detail-list.startDate')}${i18next.t('common:labelSeparator')} `}
                </span>
                <span className={classes.spanDesc}>
                    {formatDate(punch.sdt)}
                </span>
                <br />
                <span className={classes.spanTitle}>
                    {`${i18next.t('punchApproval:detail-list.endDate')}${i18next.t('common:labelSeparator')} `}
                </span>
                <span className={classes.spanDesc}>
                    {punch.edt != null ? formatDate(punch.edt) : ''}
                </span>
                <br />
            </>
        )
    }

    const getRealStartDate = (punch) => {
        return (
            punch.rsdt != null &&
            <>
                <span className={classes.spanTitle}>
                    {`${i18next.t('punchApproval:detail-list.realStartDate')}${i18next.t('common:labelSeparator')} `}
                </span>
                <span className={classes.spanDesc}>
                    {formatDate(punch.rsdt)}
                </span>
                <br />
                <span className={classes.spanTitle}>
                    {`${i18next.t('punchApproval:detail-list.realEndDate')}${i18next.t('common:labelSeparator')} `}
                </span>
                <span className={classes.spanDesc}>
                    {punch.redt != null ? formatDate(punch.redt) : ''}
                </span>
                <br />
            </>
        )
    }

    return (
        <LoadingLayer isActive={isProcessing}>
            <div id="divDetail">
                <AppBar position="fixed" className={`${classes.appBar} ${isiOS && classes.appBarIOS}`}>
                    <Toolbar className={classes.total}>
                        <Typography variant="h6">Total: {toDisplayDecimal(total)}</Typography>
                    </Toolbar>
                </AppBar>

                <div id="divListPunchDetail" className={styles.konekCard}>
                    {dataDetail != null && dataDetail.map((obj, index) =>
                        <React.Fragment key={index}>
                            {obj != null && obj.list.map((punch, index2) => {
                                let canApprove = punch.canAp;

                                if (useDoubleApprobation && approbationStructure == ApprobationStructure.PROJECT) {
                                    if (punch.myL == 2 && isLevel1ApprobationMandatory) {
                                        canApprove = canApprove && (punch.isAp1 || punch.isAp);
                                    }
                                    //canApprove = canApprove && punch.myL == 1 ? punch.canAp1 : punch.canAp2;
                                } else if (useDoubleApprobation && approbationStructure == ApprobationStructure.DEPARTMENT) {
                                    if (isLevel2Approbator && isLevel1ApprobationMandatory) {
                                        canApprove = canApprove && (punch.isAp1 || punch.isAp);
                                    }
                                }

                                return (<div key={index2} className={classes.divSection}>
                                    <div className={classes.divHeaderSection}>
                                        <span className={classes.labelDay}></span>
                                        <span className={classes.totalHourDay}>{`${toDisplayDecimal(punch.th, 2)} ${i18next.t('punchApproval:label.hours')}`}</span>
                                    </div>
                                    <div className={styles.listItem}>
                                        <div className={classes.divApproveIcon}>
                                            {(canApprove && punch.status == StatusApprove.STATUS_APPROVED || punch.status == StatusApprove.STATUS_WARNING) &&
                                                <FontAwesomeIcon icon={faCircleCheck} onClick={(e) => handleApprove(e, punch.pchId, punch.myL, index, index2, false)} className={classes.icon + ' ' + classes.iconApproved} />
                                            }
                                            {canApprove && punch.status !== StatusApprove.STATUS_APPROVED && punch.status !== StatusApprove.STATUS_WARNING &&
                                                <FontAwesomeIcon icon={faCircleCheck} onClick={(e) => handleApprove(e, punch.pchId, punch.myL, index, index2, true)} className={classes.icon} />
                                            }
                                        </div>
                                        <ListItem button onClick={() => handleClick(punch.id, punch.pchId)}>
                                            <ListItemText primary={
                                                <>
                                                    {getStartDate(punch)}
                                                    {getRealStartDate(punch)}
                                                </>
                                            } secondary={
                                                <React.Fragment>
                                                    {toDecimal(punch.tm1Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time1') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm1Cd + ' (' + toDisplayDecimal(punch.tm1Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    {toDecimal(punch.tm2Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time2') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm2Cd + ' (' + toDisplayDecimal(punch.tm2Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    {toDecimal(punch.tm3Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time3') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm3Cd + ' (' + toDisplayDecimal(punch.tm3Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    {toDecimal(punch.tm4Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time4') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm4Cd + ' (' + toDisplayDecimal(punch.tm4Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    {toDecimal(punch.tm5Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time5') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm5Cd + ' (' + toDisplayDecimal(punch.tm5Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    {toDecimal(punch.tm6Dr) > 0 &&
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.time6') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tm6Cd + ' (' + toDisplayDecimal(punch.tm6Dr, 2) + ')'}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    }
                                                    <span className={classes.spanTitle}>
                                                        {i18next.t('punchApproval:detail-list.department') + i18next.t('common:labelSeparator')}
                                                    </span>
                                                    <span className={classes.spanDesc}>
                                                        {punch.dep}
                                                    </span>
                                                    <br />
                                                    <span className={classes.spanTitle}>
                                                        {i18next.t('punchApproval:detail-list.jobTitle') + i18next.t('common:labelSeparator')}
                                                    </span>
                                                    <span className={classes.spanDesc}>
                                                        {punch.jt}
                                                    </span>
                                                    <br />
                                                    {isUseAbc && punch.pr != null && punch.pr != '' ? (
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.project') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.pr}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    ) : null}
                                                    {isUseAbc && punch.act != null && punch.act != '' ? (
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.activity') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.act}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    ) : null}
                                                    {isPayProject && punch.prP != null && punch.prP != '' ? (
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.projectPay') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.prP}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    ) : null}
                                                    {isPayActivity && punch.actP != null && punch.actP != '' ? (
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.activityPay') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.actP}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    ) : null}
                                                    {isPayTask && punch.tk != null && punch.tk != '' ? (
                                                        <div className={classes.divInput}>
                                                            <span className={classes.spanTitle}>
                                                                {i18next.t('punchApproval:detail-list.task') + i18next.t('common:labelSeparator')}
                                                            </span>
                                                            <span className={classes.spanDesc}>
                                                                {punch.tk}
                                                            </span>
                                                            <br />
                                                        </div>
                                                    ) : null}
                                                    {punch.message &&
                                                        <span className={punch.status == StatusApprove.STATUS_ERROR ? classes.divError : classes.divWarning}>
                                                            <span>{punch.message}</span>
                                                        </span>
                                                    }
                                                </React.Fragment>
                                            } />
                                        </ListItem>
                                    </div>
                                </div>)
                            }
                            )}
                        </React.Fragment>
                    )}
                </div>
                <AlertAlgo ref={alertRef} />
            </div>
        </LoadingLayer>
    );
}

export default withNamespaces(['punchApproval', 'common', 'weekday'], { wait: true })(PunchDaysDetail);